import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import ApiInspector from '../../../../components/ApiInspector';
import ComboFiltro from '../../../../components/ComboFiltro';
import { apiFetch } from '../../../../lib/api';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { showToast } from '../../../../components/Toast';
import { API_DOCS } from './DdtPage.api';
import './DdtPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDdt = 'VENDITA' | 'CANTIERE';
type StatoDdt = 'BOZZA' | 'EMESSO' | 'ANNULLATO';

interface DdtLista {
  id: number;
  societaId: number;
  sedeId: number | null;
  commessaId: number | null;
  ordineId: number | null;
  tipo: TipoDdt;
  stato: StatoDdt;
  numero: string | null;
  dataEmissione: string | null;
  dataTrasporto: string | null;
  clienteRagioneSociale: string | null;
  destinazioneDescrizione: string | null;
  destinazioneIndirizzo: string | null;
  destinazioneCap: string | null;
  destinazioneCitta: string | null;
  destinazioneProvincia: string | null;
  causaleTrasporto: string | null;
  colli: number | null;
  pesoKg: number | string | null;
  porto: string | null;
  vettore: string | null;
  annotazioni: string | null;
  movimentoMagazzinoId: number | null;
  tipoDocumento: { id: number; codice: string; nome: string } | null;
  createdAt: string;
  ordine: { id: number; numero: string; flussoCommerciale: string } | null;
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  movimentoMagazzino: { id: number; numero: number | string; anno: number; stato: string } | null;
  _count: { righe: number };
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function badgeTipo(tipo: TipoDdt): React.ReactNode {
  return tipo === 'VENDITA' ? (
    <span className="bop-badge bop-badge-info">Vendita</span>
  ) : (
    <span className="bop-badge bop-badge-warning">Cantiere</span>
  );
}

function badgeStato(stato: StatoDdt): React.ReactNode {
  switch (stato) {
    case 'BOZZA':
      return <span className="bop-badge bop-badge-neutral">Bozza</span>;
    case 'EMESSO':
      return <span className="bop-badge bop-badge-info">Emesso</span>;
    case 'ANNULLATO':
      return <span className="bop-badge bop-badge-danger">Annullato</span>;
    default:
      return <span className="bop-badge bop-badge-neutral">{stato}</span>;
  }
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function DdtPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [tipo, setTipo] = useState('');
  const [stato, setStato] = useState('');
  const [commessaId, setCommessaId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<DdtLista[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (tipo) params.set('tipo', tipo);
      if (stato) params.set('stato', stato);
      if (commessaId) params.set('commessaId', commessaId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/ddt?societaId&tipo&stato&commessaId -> {"success":true,"data":[{"id":"number","societaId":"number","tipo":"VENDITA|CANTIERE","stato":"BOZZA|EMESSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","clienteRagioneSociale":"string|null","destinazioneDescrizione":"string|null","destinazioneCitta":"string|null","movimentoMagazzinoId":"number|null","tipoDocumento":{"id":"number","codice":"string","nome":"string"},"ordine":{"id":"number","numero":"string","flussoCommerciale":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"movimentoMagazzino":{"id":"number","numero":"number","anno":"number","stato":"string"},"_count":{"righe":"number"}}]}
      const r = await apiFetch<{ success: boolean; data: DdtLista[] }>(`/api/ddt${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento dei DDT'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, tipo, stato, commessaId]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Resetta commessa se cambia società
  useEffect(() => { setCommessaId(''); }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // Ricerca testo client-side: numero, ordine, commessa, cliente
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    return items.filter(d => {
      const campi = [
        d.numero,
        d.ordine?.numero,
        d.commessa?.numero,
        d.commessa?.descrizione,
        d.commessa?.clienteRagioneSociale,
        d.clienteRagioneSociale,
        d.destinazioneDescrizione,
        d.destinazioneCitta,
      ];
      return campi.some(c => (c || '').toLowerCase().includes(q));
    });
  }, [items, q]);

  const pulisciFiltri = () => {
    setTipo('');
    setStato('');
    setCommessaId('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="ddt-lista-page">
        {/* Header — generazione massiva dal wizard, singola da ordine/distinta */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">DDT</h1>
          {can('commerciale.ddt', 'edit') && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/attivo/ddt/nuovi')}
            >
              + Genera DDT
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar ddt-lista-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input ddt-lista-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, ordine, commessa, cliente..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Tipo</label>
            <select className="bop-select" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="">Tutti</option>
              <option value="VENDITA">Vendita</option>
              <option value="CANTIERE">Cantiere</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select className="bop-select" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="EMESSO">Emesso</option>
              <option value="ANNULLATO">Annullato</option>
            </select>
          </div>

          <div className="bop-form-group ddt-lista-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare i DDT</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Tipo</th>
                  <th>Tipo doc.</th>
                  <th>Ordine</th>
                  <th>Commessa</th>
                  <th>Destinazione</th>
                  <th className="bop-text-right">Righe</th>
                  <th>Movimento magazzino</th>
                  <th>Stato</th>
                  <th>Data emissione</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          Nessun DDT trovato con i filtri attuali.
                          <br />
                          Usa "+ Genera DDT" per crearli in blocco da ordini di vendita e
                          distinte confermate, oppure genera il singolo DDT dalla scheda
                          ordine o distinta.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(d => (
                    <tr key={d.id} onClick={() => navigate(`/commerciale/attivo/ddt/${d.id}`)}>
                      <td>
                        {d.numero ? (
                          <span className="ddt-lista-numero">{d.numero}</span>
                        ) : (
                          <span className="bop-badge bop-badge-neutral">Bozza</span>
                        )}
                      </td>
                      <td>{badgeTipo(d.tipo)}</td>
                      <td className="ddt-lista-tipo-doc">
                        {d.tipoDocumento ? d.tipoDocumento.codice : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>{d.ordine?.numero || <span className="bop-text-muted">—</span>}</td>
                      <td>
                        {d.commessa ? (
                          <div className="ddt-lista-commessa">
                            <span className="ddt-lista-commessa-numero">{d.commessa.numero}</span>
                            {(d.commessa.clienteRagioneSociale || d.clienteRagioneSociale) && (
                              <span className="bop-text-muted">
                                {d.commessa.clienteRagioneSociale || d.clienteRagioneSociale}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {d.destinazioneDescrizione || d.destinazioneCitta || (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td className="bop-text-right">{d._count?.righe ?? 0}</td>
                      <td>
                        {d.movimentoMagazzino ? (
                          <span className="ddt-lista-movimento">
                            {d.movimentoMagazzino.numero}/{d.movimentoMagazzino.anno}
                          </span>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>{badgeStato(d.stato)}</td>
                      <td>{formatDateIT(d.dataEmissione)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
